'use client';

import React from 'react';
import Link from '@/components/atoms/link';
import {
  Bars3Icon,
  ClipboardDocumentListIcon,
  DocumentArrowUpIcon,
  QueueListIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Image from '@/components/atoms/Image';
import AccountNavButton from '@/components/organisms/authentication/account-nav-button';
import HintTooltip from '@/components/atoms/hint-tooltip';
import useAccount from '@/lib/hooks/useAccount';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import SearchBar from '../search-bar';
import formatCartBadge from '../../utils/format-cart-badge';
import type { Image as LogoImage } from '@/types/image';
import type { Link as LogoLink } from '@/types/link';

interface MainBarProps {
  logo?: LogoImage;
  logoLink?: LogoLink;
  cartItems: number;
  accountHref: string;
  listsHref: string;
  cartHref: string;
  onMobileMenuOpen: () => void;
  onQuickOrderOpen?: () => void;
  onBulkUploadOpen?: () => void;
}

const MainBar = ({
  logo,
  logoLink,
  cartItems,
  accountHref,
  listsHref,
  cartHref,
  onMobileMenuOpen,
  onQuickOrderOpen,
  onBulkUploadOpen,
}: MainBarProps) => {
  const translate = useTranslations();
  // EWM-602 — Quick Order + Order Upload are authenticated-only B2B ordering
  // tools (they add SKUs straight to the cart). Hide both entry points for
  // guests so an unauthenticated user can't open them. `loggedIn` is false
  // while the account state is still loading, so nothing flashes for guests;
  // it flips true only once a signed-in account resolves.
  const { loggedIn } = useAccount();

  // EWM-298 (OQ-2, Luke 2026-06-14) — Purchase Lists are Master-only; hide the
  // entry point for View-Only and guests, matching the PLP/PDP icon-hidden
  // treatment and the auth-gated Quick Order / Order Upload icons above. The
  // route itself is guarded by useRequireMaster, so this is the nav mirror.
  // `isMaster` is false while roles load, so the icon doesn't flash for
  // non-masters; it appears only once UpdateMyCarts resolves true.
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();
  const { permissions } = useAccountRoles(selectedBusinessUnit?.key);
  const isMaster = loggedIn && !!permissions.UpdateMyCarts;

  return (
    <div className="bg-primary">
      <div className="flex h-16 items-center justify-between gap-4 px-4 lg:h-[72px] lg:px-8">
        <button
          className="text-white lg:hidden"
          onClick={onMobileMenuOpen}
          aria-label={translate('common.header-menu-open')}
        >
          <Bars3Icon className="size-7" aria-hidden />
        </button>

        <Link
          href={logoLink?.href ?? '/'}
          className="shrink-0"
          underlineOnHover={false}
          aria-label={translate('common.home')}
        >
          {logo?.media?.mediaId || logo?.media?.file || logo?.src ? (
            <Image
              media={logo.media ?? {}}
              src={logo.media?.file ?? logo.src ?? ''}
              width={logo.width ?? (Number(logo.media?.width) || 160)}
              height={logo.height ?? (Number(logo.media?.height) || 40)}
              alt={logo.media?.name ?? 'Logo'}
              className="h-8 w-auto lg:h-10"
              priority
            />
          ) : (
            <span className="text-22 font-bold text-white">Electus</span>
          )}
        </Link>

        <SearchBar className="hidden flex-1 lg:flex lg:max-w-2xl xl:max-w-3xl" inputClassName="h-11" />

        <div className="flex items-center gap-4 lg:gap-6">
          <HintTooltip message={translate('common.account')}>
            <AccountNavButton accountHref={accountHref} />
          </HintTooltip>
          {loggedIn && (
            <>
              <HintTooltip message={translate('quick-order.quick-order')}>
                <button
                  type="button"
                  className="text-white hover:text-white/80"
                  onClick={onQuickOrderOpen}
                  aria-label={translate('quick-order.quick-order')}
                >
                  <ClipboardDocumentListIcon className="size-6 lg:size-7" aria-hidden />
                </button>
              </HintTooltip>
              <HintTooltip message={translate('common.order-upload')}>
                <button
                  type="button"
                  className="text-white hover:text-white/80"
                  onClick={onBulkUploadOpen}
                  aria-label={translate('common.order-upload')}
                >
                  <DocumentArrowUpIcon className="size-6 lg:size-7" aria-hidden />
                </button>
              </HintTooltip>
            </>
          )}
          {isMaster && (
            <HintTooltip message={translate('common.purchasing-lists')}>
              <Link
                href={listsHref}
                className="text-white hover:text-white/80"
                underlineOnHover={false}
                aria-label={translate('common.purchasing-lists')}
              >
                <QueueListIcon className="size-6 lg:size-7" aria-hidden />
              </Link>
            </HintTooltip>
          )}
          {/* EWM-687 — guests cannot build a cart (the /cart route redirects
              them to login), so the cart icon is redundant for them. Gate it on
              `loggedIn`, matching the auth-gated Quick Order / Order Upload icons
              above. `loggedIn` is false while account state loads, so nothing
              flashes for guests. */}
          {loggedIn && (
            <HintTooltip message={translate('common.cart')}>
              <Link
                href={cartHref}
                className="relative text-white hover:text-white/80"
                underlineOnHover={false}
                aria-label={translate('common.cart')}
              >
                <ShoppingCartIcon className="size-6 lg:size-7" aria-hidden />
                {cartItems > 0 && (
                  <span className="text-10 absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 font-bold text-white">
                    {formatCartBadge(cartItems)}
                  </span>
                )}
              </Link>
            </HintTooltip>
          )}
        </div>
      </div>

      <div className="px-4 pb-4 lg:hidden">
        <SearchBar inputClassName="h-12" />
      </div>
    </div>
  );
};

export default MainBar;
